import { ArrowRight, Lock, Globe2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { POST_TYPE_META } from "@/components/lr-direct/DirectPublishCard";

/**
 * Shared LR Direct "start" cards — the Load/Quote + Private/Open pickers.
 * Used by BOTH the legacy start dialog (LRDirectPostStartPage) and the
 * guided posting wizard (LRDirectGuidedWizardPage, Tier 3) so the two never
 * drift apart. Visuals/behavior identical to the original start page.
 */

export const POST_TYPE_OPTIONS = [
  {
    value: "load" as const,
    label: "Post a Load",
    description: "Freight that's ready to move. Brokers bid to win it — award it and go.",
    meta: POST_TYPE_META.load,
  },
  {
    value: "quote" as const,
    label: "Post a Quote",
    description: "Get indicative pricing. Brokers quote a rate so you can see the market before you commit.",
    meta: POST_TYPE_META.quote,
  },
];

export const VISIBILITY_OPTIONS = [
  {
    value: "private_only" as const,
    label: "My Private Network",
    description: "Only the brokers you've added see this. You stay private.",
    accent: "--accent-private" as const,
    Icon: Lock,
  },
  {
    value: "open_market" as const,
    label: "Open Market",
    description: "All brokers on LaneRunner can see and bid/quote. You still stay private — your info is only revealed to the broker you award.",
    accent: "--accent-open" as const,
    Icon: Globe2,
  },
];

export function PostTypeCardGrid({
  value,
  onSelect,
}: {
  value: "load" | "quote";
  onSelect: (v: "load" | "quote") => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {POST_TYPE_OPTIONS.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={cn(
              "relative flex flex-col items-start gap-2 rounded-xl border border-transparent p-4 text-left transition-all",
              "hover:scale-[1.01] active:scale-[0.99]",
              isSelected ? "ring-2 ring-offset-2" : "ring-0"
            )}
            style={{
              background: option.meta.gradient,
              color: "white",
              boxShadow: isSelected ? "0 4px 14px rgba(0,0,0,0.15)" : undefined,
            }}
            aria-pressed={isSelected}
          >
            <option.meta.Icon className="h-5 w-5" />
            <div>
              <p className="text-sm font-bold">{option.label}</p>
              <p className="text-[11px] leading-snug opacity-90 mt-0.5">{option.description}</p>
            </div>
            <ArrowRight className="h-4 w-4 absolute right-3 bottom-3 opacity-80" />
          </button>
        );
      })}
    </div>
  );
}

export function VisibilityCardGrid({
  value,
  onSelect,
}: {
  value: "private_only" | "open_market";
  onSelect: (v: "private_only" | "open_market") => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {VISIBILITY_OPTIONS.map((option) => {
        const isSelected = value === option.value;
        const accentColor = `var(${option.accent})`;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={cn(
              "relative flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all",
              "hover:scale-[1.01] active:scale-[0.99]"
            )}
            style={{
              backgroundColor: `hsl(${accentColor} / 0.08)`,
              borderColor: isSelected ? `hsl(${accentColor})` : `hsl(${accentColor} / 0.35)`,
              color: `hsl(${accentColor})`,
              boxShadow: isSelected ? `0 0 0 2px hsl(${accentColor} / 0.25)` : undefined,
            }}
            aria-pressed={isSelected}
          >
            <option.Icon className="h-5 w-5" />
            <div>
              <p className="text-sm font-bold">{option.label}</p>
              <p className="text-[11px] leading-snug opacity-90 mt-0.5" style={{ color: "hsl(var(--foreground))" }}>
                {option.description}
              </p>
            </div>
            {isSelected && (
              <span className="absolute right-3 bottom-3 text-[10px] font-bold uppercase tracking-wide">Selected</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
